import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import Loader from "react-loader-spinner";
import {
    FiChevronDown, FiChevronRight, FiEdit2, FiPlus, FiSettings,
} from "react-icons/fi";
import BottomModal from '../components/BottomModal';
import { BASE_URL } from '../constants/config';
import { buildLeagueStructure, leagueLogoCacheBust } from '../utilities/leagueStructure';
import {
    fetchAdminLeagueStructure,
    createChampionat,
    updateChampionat,
    createLeague,
    updateLeague,
    fetchAdminClientImages,
} from '../actions/leaguesActions';
import EditChampionatModal from '../components/EditChampionatModal';
import CreateChampionatModal from '../components/CreateChampionatModal';
import CreateLeagueModal from '../components/CreateLeagueModal';
import '../styles/leagueManagement.css';

const NO_CHAMPIONAT_ID = 'none';

const resolveImage = (image, bust) => {
    if (!image) return null;
    const url = image.startsWith('http') || image.startsWith('data:')
        ? image
        : BASE_URL.url + image;
    if (!bust || image.startsWith('data:')) return url;
    return `${url}${url.includes('?') ? '&' : '?'}t=${bust}`;
};

const plural = (n, one, few, many) => {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
};

export default function LeaguesManagement() {
    const dispatch = useDispatch();
    const history = useHistory();

    const [structurePayload, setStructurePayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [expanded, setExpanded] = useState({});
    const [editingChampionat, setEditingChampionat] = useState(null);
    const [createChampionatOpen, setCreateChampionatOpen] = useState(false);
    const [createLeagueFor, setCreateLeagueFor] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const payload = await dispatch(fetchAdminLeagueStructure());
            setStructurePayload(payload || { championats: [], orphan_leagues: [] });
        } catch (e) {
            console.error('Ошибка загрузки структуры лиг:', e);
            setError(e);
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => { loadData(); }, [loadData]);

    const [clientImages, setClientImages] = useState({});
    useEffect(() => {
        dispatch(fetchAdminClientImages()).then(map => setClientImages(map || {})).catch(() => {});
    }, [dispatch]);

    // Основной логотип клиента чемпионата: из структуры, иначе из списка клиентов
    const clientLogoOf = (championat) => (championat
        ? championat.client_image || clientImages[championat.client_id] || ''
        : '');

    const { championats, tree, orphanLeagues, seasonsCatalog } = useMemo(
        () => buildLeagueStructure(structurePayload),
        [structurePayload]
    );

    const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ text: '', type: 'success', visible: false, id: 0 });

    const showToast = (text, type = 'success') => {
        setToast({ text, type, visible: true, id: Date.now() });
    };

    useEffect(() => {
        if (!toast.visible) return undefined;
        const timer = setTimeout(() => {
            setToast(prev => (prev.id === toast.id ? { ...prev, visible: false } : prev));
        }, 5000);
        return () => clearTimeout(timer);
    }, [toast.id, toast.visible]);

    // Любое изменение уходит на сервер, после успеха перечитываем дерево
    const runSave = async (request, successMessage) => {
        setSaving(true);
        try {
            const result = await dispatch(request);
            await loadData();
            if (successMessage) showToast(successMessage, 'success');
            return result;
        } catch (e) {
            console.error('Ошибка сохранения:', e);
            const message = (e.response && e.response.data)
                || e.message
                || 'Не удалось сохранить изменения';
            showToast(typeof message === 'string' ? message : 'Не удалось сохранить изменения', 'error');
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleSaveChampionat = (patch) => runSave(updateChampionat({
        championat_id: editingChampionat.id,
        ...patch,
    }), 'Данные чемпионата сохранены');

    const handleCreateChampionat = async (championat) => {
        const created = await runSave(createChampionat(championat), 'Чемпионат создан');
        if (created && created.id) {
            setExpanded(prev => ({ ...prev, [created.id]: true }));
        }
    };

    const handleCreateLeague = async (league) => {
        const created = await runSave(createLeague(league), 'Лига создана');
        const id = created && (created.id || (created.data && created.data.id));
        // При создании сервер пишет image только из загруженного файла —
        // путь основного логотипа дописываем правкой лиги
        if (id && league.image && !league.logoFile) {
            await runSave(updateLeague({
                league_id: id,
                name: league.name,
                championat_id: league.championat_id,
                image: league.image,
                priority: league.priority,
                active_season: league.active_season,
                enabled: league.enabled,
            }), null);
        }
    };

    const renderLeagueRow = (league) => {
        const seasons = league.seasons || [];
        const activeSeason = league.active_season !== null
            ? seasons.find(s => s.season_id === league.active_season)
            : null;
        const seasonStatus = seasons.length === 0
            ? 'нет созданных сезонов'
            : activeSeason
                ? `активный ${activeSeason.season_name}`
                : 'активный сезон не задан';
        return (
            <div
                key={league.id}
                className="lgLeagueRow"
                role="button"
                tabIndex={0}
                onClick={() => history.push(`/leagues_management/${league.id}`)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') history.push(`/leagues_management/${league.id}`);
                }}
            >
                <div className="lgLeagueLogo">
                    {resolveImage(league.image, leagueLogoCacheBust(league.id)) ? (
                        <img
                            src={resolveImage(league.image, leagueLogoCacheBust(league.id))}
                            alt={league.name}
                        />
                    ) : (
                        <span>{(league.name || '?').charAt(0).toUpperCase()}</span>
                    )}
                </div>

                <div className="lgLeagueMain">
                    <div className="lgLeagueTitle">
                        {league.name}
                    </div>
                    <div className="lgLeagueMeta">
                        <span>
                            {seasons.length}{' '}
                            {plural(seasons.length, 'сезон', 'сезона', 'сезонов')}
                        </span>
                        <span className="lgDot">·</span>
                        <span className={!activeSeason ? 'lgMetaWarn' : undefined}>
                            {seasonStatus}
                        </span>
                        <span className="lgDot">·</span>
                        <span>приоритет {league.priority}</span>
                    </div>
                </div>

                <span className={`lgStatus ${league.enabled ? 'lgStatusOn' : 'lgStatusOff'}`}>
                    {league.enabled ? 'видна' : 'скрыта'}
                </span>

                <span className="lgLeagueAction">
                    <FiSettings />
                    Настроить
                </span>
            </div>
        );
    };

    const renderChampionat = (championat) => {
        const leagues = tree[championat.id] || [];
        const isExpanded = expanded[championat.id];

        return (
            <div key={championat.id} className="lgCard">
                <div className="lgCardHead">
                    <button
                        className="lgCardToggle"
                        onClick={() => toggle(championat.id)}
                        aria-expanded={!!isExpanded}
                    >
                        <span className="lgChevron">
                            {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                        </span>
                        <span className="lgCardTitle">
                            {championat.name}
                        </span>
                        <span className="lgCardMeta">
                            {leagues.length} {plural(leagues.length, 'лига', 'лиги', 'лиг')}
                            <span className="lgDot">·</span>
                            приоритет {championat.priority}
                        </span>
                    </button>

                    <button
                        className="lgGhostBtn"
                        disabled={saving}
                        onClick={(e) => { e.stopPropagation(); setEditingChampionat(championat); }}
                    >
                        <FiEdit2 />
                        Изменить
                    </button>
                </div>

                {isExpanded && (
                    <div className="lgCardBody">
                        {leagues.length > 0 ? (
                            <div className="lgLeagueList">{leagues.map(renderLeagueRow)}</div>
                        ) : (
                            <p className="lgEmpty">В этом чемпионате пока нет лиг.</p>
                        )}
                        <button
                            className="lgAddLeagueBtn"
                            disabled={saving}
                            onClick={() => setCreateLeagueFor(championat)}
                        >
                            <FiPlus />
                            Создать лигу в «{championat.name}»
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="layoutWrapper">
            <div className="matchCenterWrapper">
                <div className="matchCenterHeader">
                    <span className="matchCenterName">Управление лигами</span>
                </div>

                <div className="lgPage">
                    <div className="lgSectionHead">
                        <div>
                            <h2>Чемпионаты</h2>
                            <p>
                                Внутри каждого чемпионата — его лиги. Нажмите на лигу,
                                чтобы настроить сезоны и составы.
                            </p>
                        </div>
                        <button
                            className="lgPrimaryBtn"
                            disabled={saving}
                            onClick={() => setCreateChampionatOpen(true)}
                        >
                            <FiPlus />
                            Создать чемпионат
                        </button>
                    </div>

                    {loading ? (
                        <div className="lgLoader">
                            <Loader type="Oval" color="#04a9f5" height={64} width={64} />
                            <p>Загрузка структуры лиг…</p>
                        </div>
                    ) : error ? (
                        <div className="lgError">
                            Ошибка загрузки:{' '}
                            {(error.response && error.response.data) || error.message || 'неизвестная ошибка'}
                        </div>
                    ) : championats.length === 0 && orphanLeagues.length === 0 ? (
                        <div className="lgEmptyState">
                            <p>Чемпионатов пока нет.</p>
                            <button
                                className="lgPrimaryBtn"
                                onClick={() => setCreateChampionatOpen(true)}
                            >
                                <FiPlus />
                                Создать первый чемпионат
                            </button>
                        </div>
                    ) : (
                        <div className="lgList">
                            {championats.map(renderChampionat)}

                            {orphanLeagues.length > 0 && (
                                <div className="lgCard lgCardMuted">
                                    <div className="lgCardHead">
                                        <button
                                            className="lgCardToggle"
                                            onClick={() => toggle(NO_CHAMPIONAT_ID)}
                                            aria-expanded={!!expanded[NO_CHAMPIONAT_ID]}
                                        >
                                            <span className="lgChevron">
                                                {expanded[NO_CHAMPIONAT_ID]
                                                    ? <FiChevronDown /> : <FiChevronRight />}
                                            </span>
                                            <span className="lgCardTitle">Без чемпионата</span>
                                            <span className="lgCardMeta">
                                                {orphanLeagues.length}{' '}
                                                {plural(orphanLeagues.length, 'лига', 'лиги', 'лиг')}
                                            </span>
                                        </button>
                                    </div>
                                    {expanded[NO_CHAMPIONAT_ID] && (
                                        <div className="lgCardBody">
                                            <div className="lgLeagueList">
                                                {orphanLeagues.map(renderLeagueRow)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <EditChampionatModal
                isOpen={!!editingChampionat}
                championat={editingChampionat}
                onClose={() => setEditingChampionat(null)}
                onSave={handleSaveChampionat}
            />

            <CreateChampionatModal
                isOpen={createChampionatOpen}
                onClose={() => setCreateChampionatOpen(false)}
                onCreate={handleCreateChampionat}
                existingNames={championats.map(ch => ch.name)}
            />

            <CreateLeagueModal
                isOpen={!!createLeagueFor}
                championat={createLeagueFor}
                seasonsCatalog={seasonsCatalog}
                existingNames={createLeagueFor
                    ? (tree[createLeagueFor.id] || []).map(l => l.name)
                    : []}
                onClose={() => setCreateLeagueFor(null)}
                onCreate={handleCreateLeague}
                clientLogo={clientLogoOf(createLeagueFor)}
            />

            <div className={`matchResultsModalWrapper${toast.visible ? ' matchResultsModalWrapperActive' : ''}`}>
                <BottomModal text={toast.text} type={toast.type} />
            </div>
        </div>
    );
}
